import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { IoEyeOutline, IoSearchOutline, IoChevronBack, IoChevronForward, IoArrowUp, IoArrowDown, IoTime } from 'react-icons/io5';
import { API_URL } from '../../config';
import { EmptyState, ErrorState, Skeleton, PageHeader } from '../../shared-ui/States';
import '../../shared-ui/crane-tokens.css';
import './Employees.css';

const Employees = () => {
    const [employees, setEmployees] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [sortOrder, setSortOrder] = useState('recent');

    useEffect(() => {
        fetchEmployees(1);
    }, []);

    const fetchEmployees = async (page = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('zTaskAuthToken');
            const res = await axios.get(`${API_URL}/hr/employees?page=${page}&limit=1000`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = res.data.data || (Array.isArray(res.data) ? res.data : []);
            const meta = res.data.pagination || {
                currentPage: page,
                totalPages: Math.ceil((data.length || 0) / 1000) || 1,
                totalRecords: data.length || 0
            };

            setEmployees(data);
            setPagination(meta);
            setError(null);
        } catch (err) {
            console.error("Error fetching employees:", err);
            setError("Failed to load employee directory.");
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchEmployees(newPage);
        }
    };

    const filteredEmployees = useMemo(() => {
        let result = employees.filter(emp =>
            (`${emp.first_name || ''} ${emp.last_name || ''}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.emp_code || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (sortOrder === 'asc') {
            result = [...result].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
        } else if (sortOrder === 'desc') {
            result = [...result].sort((a, b) => (b.first_name || '').localeCompare(a.first_name || ''));
        } else {
            result = [...result].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        return result;
    }, [employees, searchTerm, sortOrder]);

    const getStatusColor = (status) => {
        if (!status) return 'status-indicator';
        const s = status.toLowerCase();
        if (s.includes('active')) return 'status-indicator status-active';
        return 'status-indicator status-inactive';
    };

    return (
        <div className="employees-page crane-page">
            <PageHeader
                crumbs={['Operations', 'ZHR', 'Workforce Roster']}
                title="Personnel workforce roster"
                description={`${pagination.totalRecords || 0} employees from live HR API.`}
            />
            <div className="controls-section crane-filters">
                <div className="search-bar">
                    <IoSearchOutline className="search-icon" size={18} />
                    <input
                        type="text"
                        placeholder="Search by name, code or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input crane-input"
                    />
                </div>
                <div className="sort-controls">
                    <span className="sort-label">Sort:</span>
                    <div className="sort-buttons">
                        <button type="button" className={`sort-btn crane-btn ${sortOrder === 'recent' ? 'active' : ''}`} onClick={() => setSortOrder('recent')}>
                            <IoTime size={16} /> Recent
                        </button>
                        <button type="button" className={`sort-btn crane-btn ${sortOrder === 'asc' ? 'active' : ''}`} onClick={() => setSortOrder('asc')}>
                            <IoArrowUp size={16} /> A-Z
                        </button>
                        <button type="button" className={`sort-btn crane-btn ${sortOrder === 'desc' ? 'active' : ''}`} onClick={() => setSortOrder('desc')}>
                            <IoArrowDown size={16} /> Z-A
                        </button>
                    </div>
                </div>
            </div>

            {loading && <Skeleton rows={6} />}
            {error && (
                <ErrorState
                    title="Directory unavailable"
                    description={error}
                    onRetry={() => fetchEmployees(pagination.currentPage)}
                />
            )}
            {!loading && !error && filteredEmployees.length === 0 && (
                <EmptyState
                    title="No employees"
                    description={searchTerm ? 'No employees match your search.' : 'HR employee records will appear here when available.'}
                />
            )}
            {!loading && !error && filteredEmployees.length > 0 && (
                <div className="crane-card" style={{ overflow: 'auto' }}>
                    <table className="employees-table crane-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Department</th>
                                <th>Designation</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEmployees.map((emp) => (
                                <tr key={emp.id || emp.emp_code}>
                                    <td>{emp.emp_code || '—'}</td>
                                    <td style={{ fontWeight: 600 }}>{`${emp.first_name || ''} ${emp.last_name || ''}`.trim() || '—'}</td>
                                    <td>{emp.email || '—'}</td>
                                    <td>{emp.department_name || emp.department || '—'}</td>
                                    <td>{emp.designation_name || emp.designation || '—'}</td>
                                    <td>
                                        <span className={getStatusColor(emp.status || emp.employment_status)}>
                                            {emp.status || emp.employment_status || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <button type="button" className="crane-btn" title="View" aria-label="View employee">
                                            <IoEyeOutline />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pagination.totalPages > 1 && (
                <div className="pagination-controls" style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
                    <button type="button" className="crane-btn" disabled={pagination.currentPage <= 1} onClick={() => handlePageChange(pagination.currentPage - 1)}>
                        <IoChevronBack /> Prev
                    </button>
                    <span>Page {pagination.currentPage} / {pagination.totalPages}</span>
                    <button type="button" className="crane-btn" disabled={pagination.currentPage >= pagination.totalPages} onClick={() => handlePageChange(pagination.currentPage + 1)}>
                        Next <IoChevronForward />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Employees;
